
import { useJobFilters } from "@/hooks/useJobFilters";
import { useJobs } from "@/hooks/useJobs";
import { JobListHeader } from "./JobListHeader";
import { JobListFilters } from "./JobListFilters";
import { JobCard } from "./JobCard";
import { JobListSkeleton } from "./JobListSkeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useSearchParams } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useMemo, useEffect } from "react";
import { MobileFilterDrawer } from "./MobileFilterDrawer";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { DataPagination } from "@/components/ui/data-pagination";
import { TooltipProvider } from "@/components/ui/tooltip";

function JobList() {
  const { organizationId } = useParams();
  const { filters, setters, filterJobs, applyFilters, resetFilters } = useJobFilters();
  
  // Use URL search params instead of local state for pagination
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  
  const PAGE_SIZE = 9; // Match the page size in useJobs
  
  // Only show published jobs that are marked for job board display
  const { jobs, totalCount, isLoading } = useJobs(false, {
    searchQuery: filters.searchQuery,
    locationFilter: filters.locationFilter,
    skillFilter: filters.skillFilter,
    industryFilter: filters.industryFilter,
    workArrangementFilter: filters.workArrangementFilter,
    salaryRange: filters.salaryRange,
    page: currentPage,
    pageSize: PAGE_SIZE
  });

  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setSearchParams({ page: "1" });
    }
  }, [filters.searchQuery, filters.locationFilter, filters.skillFilter, 
      filters.industryFilter, filters.workArrangementFilter, filters.salaryRange]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.locationFilter !== "all") count++;
    if (filters.skillFilter !== "all") count++;
    if (filters.companyFilter !== "all") count++;
    if (filters.roleFilter !== "all") count++;
    if (filters.workArrangementFilter !== "all") count++;
    if (filters.industryFilter !== "all") count++;
    // Add count for salary range if it's not at the default values
    if (filters.salaryRange[0] > 0 || filters.salaryRange[1] < 200000) count++;
    return count;
  }, [filters]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  }, [totalCount]);

  // Handle page change - update URL params
  const handlePageChange = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page.toString());
      return newParams;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <main className="w-full min-h-screen bg-background">
        <div className="container py-6">
          <JobListSkeleton />
        </div>
      </main>
    );
  }

  let displayedJobs = jobs;

  if (organizationId) {
    displayedJobs = displayedJobs.filter(job => job.organization?.id === organizationId);
  }

  return (
    <TooltipProvider>
      <main className="w-full min-h-screen bg-background">
        <div className="container max-w-7xl mx-auto py-6 px-6 md:px-12 lg:px-24 space-y-6 md:space-y-8">
          <JobListHeader 
            activeFilterCount={activeFilterCount}
            onFilterClick={() => setIsFilterDrawerOpen(true)}
          />
          <div className="grid grid-cols-12 gap-6">
            {!isMobile && (
              <div className="col-span-12 md:col-span-3">
                <JobListFilters
                  filters={filters}
                  setters={setters}
                  onApplyFilters={applyFilters}
                  onResetFilters={resetFilters}
                />
              </div>
            )}
            <div className="col-span-12 md:col-span-9">
              {isMobile && (
                <div className="mb-4 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search jobs..."
                    value={filters.searchQuery}
                    onChange={(e) => {
                      setters.setSearchQuery(e.target.value);
                      if (e.target.value === "") {
                        applyFilters();
                      }
                    }}
                    className="w-full pl-10"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        applyFilters();
                      }
                    }}
                  />
                </div>
              )}
              
              {displayedJobs.length === 0 ? (
                <div className="text-center py-8">
                  <h3 className="text-lg font-semibold text-foreground mb-2">No jobs found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your filters or check back later for new opportunities
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {displayedJobs.map((job) => (
                      <JobCard key={job.id} job={job} />
                    ))}
                  </div>
                  
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                      <DataPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {isMobile && (
            <MobileFilterDrawer
              open={isFilterDrawerOpen}
              onOpenChange={setIsFilterDrawerOpen}
              filters={filters}
              setters={setters}
              onApplyFilters={applyFilters}
              onResetFilters={resetFilters}
              uniqueLocations={Array.from(new Set(jobs?.map(job => job.location) || [])).filter(Boolean)}
              uniqueIndustries={Array.from(new Set(jobs?.map(job => job.industry) || [])).filter(Boolean)}
              uniqueSkills={Array.from(new Set(jobs?.flatMap(job => job.key_skills || []) || [])).filter(Boolean)}
              uniqueCompanies={Array.from(new Set(jobs?.map(job => job.organization?.organization) || [])).filter(Boolean)}
            />
          )}
        </div>
      </main>
    </TooltipProvider>
  );
}

export default JobList;
